import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANG, LANGUAGES, TRANSLATIONS } from "@/i18n/translations";

const LS_KEY = "motogo.lang";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: TRANSLATIONS[DEFAULT_LANG],
  languages: LANGUAGES,
});

const pickInitialLang = () => {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const saved = window.localStorage.getItem(LS_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
  } catch (e) { /* ignore */ }
  const browser = (navigator.language || "").slice(0, 2).toLowerCase();
  if (TRANSLATIONS[browser]) return browser;
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(DEFAULT_LANG);

  // Hydrate from localStorage / browser language after mount.
  useEffect(() => {
    setLangState(pickInitialLang());
  }, []);

  // Reflect on <html lang> for a11y/SEO.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!TRANSLATIONS[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(LS_KEY, next);
    } catch (e) { /* ignore */ }
  }, []);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG],
      languages: LANGUAGES,
    }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
